import type { JSX } from 'react';
import { useState } from 'react';
import {
  ensureAiMarker,
  validateAvatarUrl,
  validateDisplayName,
  validateTeamsMeetingUrl,
  type SessionLimits,
} from '@teams-avatar/shared';
import { api, ApiError } from '../lib/api';
import { Button, Card, Field, Input } from './ui';

interface Props {
  limits: SessionLimits | null;
  onStarted: () => void;
}

const EMPTY = { meetingUrl: '', avatarUrl: '', displayName: '', autoLeaveMinutes: '' };

type Quelle = 'url' | 'godialogue';

export function StartSessionForm({ limits, onStarted }: Props): JSX.Element {
  const [form, setForm] = useState(EMPTY);
  const [quelle, setQuelle] = useState<Quelle>('url');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const atLimit = limits !== null && limits.activeSessions >= limits.maxConcurrentSessions;

  /**
   * Clientseitige Pruefung ist Komfort: dieselben Funktionen laufen im Backend
   * noch einmal und sind dort verbindlich.
   */
  const localCheck = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const meeting = validateTeamsMeetingUrl(form.meetingUrl);
    if (!meeting.ok) errors['meetingUrl'] = meeting.error ?? 'Ungueltig.';
    const name = validateDisplayName(form.displayName);
    if (!name.ok) errors['displayName'] = name.error ?? 'Ungueltig.';
    if (limits !== null) {
      const avatar = validateAvatarUrl(form.avatarUrl, limits.avatarUrlAllowlist);
      if (!avatar.ok) errors['avatarUrl'] = avatar.error ?? 'Ungueltig.';
    }
    return errors;
  };

  /** Wie localCheck, aber ohne die AVA-Adresse. */
  const localCheckOhneUrl = (): Record<string, string> => {
    const errors = localCheck();
    delete errors['avatarUrl'];
    return errors;
  };

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);

    // Bei goDialogue gibt es keine Adresse zu pruefen — sie steht auf dem Server.
    const local = quelle === 'godialogue' ? localCheckOhneUrl() : localCheck();
    setFieldErrors(local);
    if (Object.keys(local).length > 0) return;

    setBusy(true);
    try {
      const minutes = form.autoLeaveMinutes.trim();
      await api.createSession({
        meetingUrl: form.meetingUrl.trim(),
        source: quelle,
        // Bei goDialogue leer: die Adresse setzt der Server.
        avatarUrl: quelle === 'godialogue' ? '' : form.avatarUrl.trim(),
        displayName: form.displayName.trim(),
        // startedBy setzt das Backend aus dem Anmelde-Cookie.
        startedBy: '',
        autoLeaveAfterSeconds: minutes === '' ? null : Number(minutes) * 60,
      });
      setForm(EMPTY);
      setFieldErrors({});
      onStarted();
    } catch (cause) {
      if (cause instanceof ApiError) {
        setFieldErrors(cause.fields);
        setError(Object.keys(cause.fields).length > 0 ? null : cause.message);
      } else {
        setError(String(cause));
      }
    } finally {
      setBusy(false);
    }
  };

  const field = (key: keyof typeof EMPTY): string | undefined => fieldErrors[key];

  return (
    <Card>
      <form onSubmit={(e) => void submit(e)} className="space-y-4">
        <div>
          <h2 className="text-base text-fg">AVA in ein Meeting schicken</h2>
          <p className="mt-0.5 text-sm text-fg-muted">
            {quelle === 'godialogue'
              ? 'Meeting-Link einfuegen, fertig — AVA kommt aus eurem Realtime-Werkzeug.'
              : 'Meeting-Link und AVA-Link einfuegen, fertig.'}
          </p>
        </div>

        {/*
          Auswahl der Quelle. Nur sichtbar, wenn der Server fuer goDialogue
          eingerichtet ist — eine Auswahl anzubieten, die dann scheitert, waere
          schlechter als keine.
        */}
        {limits?.goDialogueReady === true && (
          <div className="inline-flex rounded-lg border border-border p-0.5" role="group">
            {(
              [
                ['url', 'AVA-Link'],
                ['godialogue', 'goDialogue 2.0'],
              ] as const
            ).map(([wert, text]) => (
              <button
                key={wert}
                type="button"
                onClick={() => setQuelle(wert)}
                aria-pressed={quelle === wert}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  quelle === wert ? 'bg-brand text-fg' : 'text-fg-subtle hover:bg-bg-muted'
                }`}
              >
                {text}
              </button>
            ))}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Teams-Meeting-Link" htmlFor="meetingUrl" error={field('meetingUrl')}>
            <Input
              id="meetingUrl"
              value={form.meetingUrl}
              onChange={(e) => setForm({ ...form, meetingUrl: e.target.value })}
              placeholder="https://teams.microsoft.com/l/meetup-join/…"
            />
          </Field>

          {quelle === 'godialogue' ? (
            <Field label="Avatar" hint="Der Worker meldet sich dort selbst an. Zugangsdaten liegen auf dem Server und gehen nie an den Browser.">
              <div className="rounded-lg border border-border bg-bg-elevated px-3 py-2 text-sm text-fg-subtle">
                goDialogue 2.0 — euer Realtime-Werkzeug
              </div>
            </Field>
          ) : (
          <Field
            label="AVA-URL"
            htmlFor="avatarUrl"
            error={field('avatarUrl')}
            hint={
              limits === null
                ? undefined
                : limits.avatarUrlAllowlist.length === 0
                  ? 'Jede https-Adresse ist zugelassen.'
                  : `Erlaubte Hosts: ${limits.avatarUrlAllowlist.join(', ')}`
            }
          >
            <Input
              id="avatarUrl"
              value={form.avatarUrl}
              onChange={(e) => setForm({ ...form, avatarUrl: e.target.value })}
              placeholder="https://web.goava.ai/…"
            />
          </Field>
          )}

          <Field
            label="Anzeigename im Meeting"
            htmlFor="displayName"
            error={field('displayName')}
            hint={
              form.displayName.trim().length > 0
                ? `Erscheint als: ${ensureAiMarker(form.displayName)}`
                : 'Die KI-Kennzeichnung wird automatisch angehaengt und ist nicht abschaltbar.'
            }
          >
            <Input
              id="displayName"
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              placeholder="Lea"
            />
          </Field>

          <Field
            label="Auto-Leave nach (Minuten, optional)"
            htmlFor="autoLeave"
            error={field('autoLeaveMinutes')}
            hint={
              limits === null
                ? undefined
                : `Leer = Standard ${Math.round(limits.defaultAutoLeaveSeconds / 60)} Minuten.`
            }
          >
            <Input
              id="autoLeave"
              type="number"
              min={1}
              value={form.autoLeaveMinutes}
              onChange={(e) => setForm({ ...form, autoLeaveMinutes: e.target.value })}
              className="sm:w-40"
            />
          </Field>
        </div>

        {error !== null && (
          <p className="rounded-lg bg-danger/10 px-3 py-2 text-sm text-danger">{error}</p>
        )}

        {atLimit && (
          <p className="rounded-lg bg-warning/15 px-3 py-2 text-sm text-[color:#8a5a00]">
            Das Limit von {limits?.maxConcurrentSessions} gleichzeitigen Sessions ist erreicht.
            Bitte zuerst eine Session beenden.
          </p>
        )}

        <Button type="submit" variant="primary" disabled={busy || atLimit}>
          {busy ? 'Startet …' : 'Beitreten'}
        </Button>
      </form>
    </Card>
  );
}
